import { useEffect, useState } from "react";
import PageShell from "@/components/brand/PageShell";
import { api } from "@/lib/api";

/* Fallbacks in case the CMS record has never been touched. Verbatim from Phase 0 legal copy. */
const DEFAULT_TERMS = `These Terms of Service (the "Terms") govern your access to and use of the SwingTrust educational platform, including any subscriptions, content, tools, and services made available through swingtrust.com (collectively, the "Platform"). By creating an account or purchasing a subscription, you agree to be bound by these Terms and by our Privacy Policy and Risk Disclosure.

## 1. Educational purpose

SwingTrust provides **educational content only**. Nothing published on the Platform — including the curriculum, videos, live sessions, chat, community discussions, quizzes, or any interaction with the instructor or staff — constitutes financial, investment, legal, tax, or accounting advice, or a recommendation to buy, sell, or hold any security or financial instrument. We do not offer trade signals, trade calls, or portfolio management services.

## 2. Enrollment, payment, and refunds

Enrollment is offered through two subscription plans: a monthly plan at $350 per month, and a yearly plan at $3,000 per year. Both plans are processed through Stripe, our payment provider.

**All enrollments are final. No refunds are offered on either plan.** The monthly plan is designed to serve as the trial — one month at $350 gives you a full look at the program before committing to the yearly plan.

## 3. Account and eligibility

You must be at least 18 years old to open an account. You are responsible for maintaining the confidentiality of your credentials and for all activity that occurs under your account.

## 4. Acceptable use

The curriculum, live-session recordings, quizzes, journals, and all other content on the Platform are the intellectual property of SwingTrust or its licensors.

## 5. No performance guarantees

Any performance figures, case studies, backtests, historical charts, or examples presented on the Platform are illustrative only. Past performance is not indicative of future results.

## 6. Third-party services

We rely on third-party service providers to operate the Platform, including Stripe (payments), Google (authentication via OAuth), and standard cloud infrastructure and analytics providers.

## 7. Termination

We may suspend or terminate accounts that violate these Terms, disrupt other members, misuse the Platform, attempt to circumvent enrollment pacing, or engage in fraudulent payment activity.

## 8. Limitation of liability

To the maximum extent permitted by law, SwingTrust, its officers, employees, contractors, and affiliates will not be liable for any indirect, incidental, consequential, or punitive damages.

## 9. Changes to these Terms

We may update these Terms from time to time. Material changes will be communicated to enrolled members via the dashboard.

## 10. Contact

Questions about these Terms may be directed to legal@swingtrust.com.`;

const DEFAULT_PRIVACY = `SwingTrust ("we," "our," "us") takes your privacy seriously. This Privacy Policy describes what personal information we collect when you use the SwingTrust platform, how we use and protect it, and the choices you have regarding your data.

## 1. Information we collect

**Account information.** When you register, we collect your name, email address, and — if you sign in with Google — your Google account identifier and, optionally, your profile picture. Passwords are stored only as bcrypt hashes.

**Billing information.** When you enroll, Stripe collects and processes your payment information directly.

**Curriculum activity.** We record your curriculum progress, quiz results, journal entries, and interactions with live sessions.

## 2. Cookies and similar technologies

We use a single first-party HTTP-only session cookie (swingtrust_session) to keep you signed in.

## 3. How we use information

We use your information to operate the Platform, deliver the curriculum, personalize your progression, process payments, send transactional messages, prevent fraud, and meet our legal obligations.

## 4. Third-party processors

We share personal data only with the service providers necessary to operate the Platform: Stripe for payments, Google for OAuth authentication, and standard cloud infrastructure providers.

## 5. Data retention

We retain account and progress data for as long as your account is active. If you close your account, we delete personal identifiers within 90 days.

## 6. Your rights

You may request access to, correction of, or deletion of your personal data at any time. Contact privacy@swingtrust.com to exercise any of these rights.

## 7. Security

We follow industry-standard practices to protect personal data, including encrypted transport (HTTPS everywhere), bcrypt-hashed passwords, and restricted access to production systems.

## 8. Children

SwingTrust is not intended for use by anyone under 18. We do not knowingly collect data from children.

## 9. Contact

Privacy-related questions may be directed to privacy@swingtrust.com.`;

const DEFAULT_RISK = `**Trading financial instruments involves substantial risk of loss and is not suitable for every investor. You may lose some or all of the capital you invest.** Please read this Risk Disclosure carefully before enrolling in SwingTrust or acting on any content published on the Platform.

## 1. Educational content only

SwingTrust publishes educational material designed to teach a disciplined, structured approach to swing trading. Nothing published on the Platform is a recommendation to buy, sell, or hold any specific security, derivative, cryptocurrency, or financial instrument.

## 2. No performance guarantee

Any historical returns, backtests, charts, or case studies presented on the Platform are illustrative and do not represent actual client accounts. Past performance is not indicative of, and offers no guarantee of, future results.

## 3. Independent judgment

**You alone are responsible for the trading decisions you make.** Before applying anything you learn on the Platform to a live account, consider your personal circumstances, objectives, financial situation, and level of experience.

## 4. Market risk factors

Swing trading is subject to price volatility, liquidity risk, gap risk over weekends and overnight, execution slippage, dividend and corporate-action risk, currency risk, and systemic market risk.

## 5. Third-party data and brokers

SwingTrust is education-only. We do not provide real-time market data, order routing, execution venues, or brokerage accounts. Students are responsible for choosing and using their own broker.

## 6. Suitability

Swing trading is not suitable for capital you cannot afford to lose. Never trade with funds required for essential living expenses, debt servicing, retirement obligations, or emergency reserves.

## 7. Acknowledgment

By enrolling in SwingTrust, you acknowledge that you have read, understood, and accepted this Risk Disclosure, and that you understand SwingTrust is an educational service and not a source of trade recommendations, signals, or financial advice.`;

/**
 * Ultra-light markdown -> React renderer.
 * Supports: ## headings (h2), **bold** inline, and paragraph splitting on blank lines.
 * Good enough for institutional legal copy without shipping a full markdown lib.
 */
function renderMarkdown(md) {
    if (!md) return null;
    const blocks = md.split(/\n\s*\n/);
    return blocks.map((block, i) => {
        const trimmed = block.trim();
        if (trimmed.startsWith("## ")) {
            return (
                <h2 key={i} className="font-serif text-2xl mt-8 mb-2" style={{ color: "var(--st-navy)" }}>
                    {trimmed.slice(3)}
                </h2>
            );
        }
        // Render **bold** by splitting.
        const parts = trimmed.split(/(\*\*[^*]+\*\*)/g);
        return (
            <p key={i}>
                {parts.map((part, j) => {
                    if (part.startsWith("**") && part.endsWith("**")) {
                        return <strong key={j} style={{ color: "var(--st-navy)" }}>{part.slice(2, -2)}</strong>;
                    }
                    return <span key={j}>{part}</span>;
                })}
            </p>
        );
    });
}

function LegalPage({ title, eyebrow, testId, fallback, settingsKey }) {
    const [settings, setSettings] = useState(null);
    useEffect(() => {
        api.publicSiteSettings().then(setSettings).catch(() => setSettings({}));
    }, []);
    const body = settings?.[settingsKey] || fallback;
    const updated = settings?.legal_updated_label || "February 2026";

    return (
        <PageShell>
            <section className="max-w-3xl mx-auto px-6 py-16" data-testid={testId}>
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>{eyebrow}</div>
                <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>{title}</h1>
                <p className="tracking-caps text-[10px] font-semibold mt-3" style={{ color: "var(--st-silver-500)" }}>
                    Last updated · {updated}
                </p>
                <div className="mt-8 space-y-5 text-base leading-relaxed" style={{ color: "var(--st-ink)" }}>
                    {renderMarkdown(body)}
                </div>
            </section>
        </PageShell>
    );
}

export function Terms() {
    return <LegalPage title="Terms of Service" eyebrow="Legal" testId="terms-page" fallback={DEFAULT_TERMS} settingsKey="legal_terms_markdown" />;
}
export function Privacy() {
    return <LegalPage title="Privacy Policy" eyebrow="Legal" testId="privacy-page" fallback={DEFAULT_PRIVACY} settingsKey="legal_privacy_markdown" />;
}
export function RiskDisclosure() {
    return <LegalPage title="Risk Disclosure" eyebrow="Legal" testId="risk-page" fallback={DEFAULT_RISK} settingsKey="legal_risk_markdown" />;
}
